import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SocialLoginUser } from 'avn/interfaces/socialloginuser';
import { User } from 'avn/models/user';
import { AuthAppService } from 'avn/services/auth-app/auth-app.service';
import { Constants } from 'avn/services/constants';
import { UserService } from 'avn/services/user.service';
import { alphanumericValidator } from 'avn/shared/directives/alphanumeric.validator';
import { SharedCompsService } from 'avn/shared/shared-comps.service';
import { ValidatorComponent } from 'avn/shared/validator/validator.component';

@Component({
  selector: 'app-customer-sign-up',
  templateUrl: './customer-sign-up.component.html',
  styleUrls: ['./customer-sign-up.component.scss']
})
export class CustomerSignUpComponent extends ValidatorComponent implements OnDestroy {
  loading = false;
  redirectUrl = '';
  isProviderPage = false;
  socialuser: SocialLoginUser;
  wellbeingData: any;
  selectedTab = 'signin';

  signUpError = '';
  signUpSuccess = '';
  phoneObject: { phone_number: string; country_code: string } = { phone_number: '', country_code: '+61' };

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private router: Router,
    private snackBar: SharedCompsService,
    private authService: AuthAppService
  ) {
    super();
  }

  signUpForm = this.fb.group({
    email: ['', [Validators.required, Validators.pattern(Constants.EMAIL_VALIDATION)]],
    first_name: ['', [Validators.required, Validators.minLength(2), alphanumericValidator]],
    last_name: ['', [Validators.required, Validators.minLength(2), alphanumericValidator]],
    password: [
      '',
      [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(25),
        this.uppercaseValidator(),
        this.lowercaseValidator(),
        this.numberValidator(),
        this.specialCharacterValidator()
      ]
    ],
    phone_no: ['', [Validators.required]],
    country_code: ['', [Validators.required]],
    confirm_check: ['', [Validators.required]],
    notifications_check: ['']
  });

  checkPasswordError(errorType: string): boolean {
    return !!this.signUpForm.controls['password'].errors?.[errorType];
  }

  passwordMaxLengthCheck() {
    if (
      this.signUpForm?.controls['password']?.value?.length &&
      this.signUpForm?.controls['password']?.value?.length > 25
    ) {
      return true;
    }
    return false;
  }
  passwordMinLengthCheck() {
    if (
      this.signUpForm?.controls['password']?.value?.length &&
      this.signUpForm?.controls['password']?.value?.length > 7
    ) {
      return true;
    }
    return false;
  }

  signUpUser() {
    // console.log(this.signUpForm.getRawValue());
    this.loading = true;
    const user_obj: User = new User();
    user_obj.email = this.signUpForm.get('email')?.value ?? '';
    user_obj.first_name = this.signUpForm.get('first_name')?.value ?? '';
    user_obj.last_name = this.signUpForm.get('last_name')?.value ?? '';
    user_obj.password = this.signUpForm.get('password')?.value ?? '';
    user_obj.phone_no = this.signUpForm.get('phone_no')?.value ?? '';

    this.signUpError = '';
    user_obj.is_customer = true;
    user_obj.is_owner = false;
    user_obj.is_practitioner = false;
    user_obj.is_active = true;
    user_obj.provider = 'normal';
    this.loading = true;

    const responseObs = this.userService.addUser(user_obj);
    responseObs.subscribe({
      next: (response) => {
        this.loading = false;
        this.snackBar.showSnackBar('You are successfully registered, Please login', 5);
        this.loginResponse(response);
      },
      error: (error) => {
        this.loading = false;
        this.snackBar.showSnackBar(error.error.message || 'User with this email already exists.', 5);
      }
    });
    this.subscriptions.push(responseObs);
  }

  subscriptions: any[] = [];
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      try {
        sub.unsubscribe();
      } catch (error) {
        console.info('');
      }
    });
  }

  loginResponse(response: any) {
    if (response.status === 500) {
      this.loading = false;
      let errormsg = '';
      const idx = response.Message.indexOf('Your Account is Locked. Reset your Login using Forgot Password? link');
      if (idx !== -1) {
        errormsg = 'Your Account is locked. You can unlock by resetting your password';
      } else {
        errormsg = 'Incorrect email or password';
      }
      this.snackBar.showSnackBar(errormsg);
      this.authService.purgeAuth();
      return;
    }
    this.snackBar.showSnackBar('Login Successful', 5);
    this.successHandler(response);
    this.navigateToPage(response);
  }

  successHandler(response: any) {
    this.authService.setAuth(response?.auth_token);
    const user = <User>response?.user;
    if (response.header_detail) {
      user.header_detail = response.header_detail;
      user.businesses = response.header_detail.businesses;
      this.wellbeingData = response.header_detail.wellbeing;
      this.authService.setWellbeingDetails(response.header_detail.wellbeing);
    }
    this.authService.setTrinityLoggedUser(user);
    this.loading = false;
  }

  navigateToPage(response: any) {
    if (response.user.is_wellbeing_owner) {
      // wellbeing user
      this.router.navigate(['/wellbeing']);
    } else if (response.user.is_owner && response.header_detail?.businesses?.length > 0) {
      // business user
      const businesses = response.header_detail.businesses;
      const business_id = businesses[0].uuid;
      this.router.navigate(['/business/' + business_id]);
    } else {
      if (this.redirectUrl) {
        this.router.navigate([this.redirectUrl]);
      } else {
        this.router.navigate(['/']);
      }
    }
  }

  phoneGroupEmit(e: FormGroup) {
    const values = e.getRawValue();
    this.phoneNumber?.setValue(values.phone_number);
    this.countryCode?.setValue(values.country_code);
    const currentErrors = this.phoneNumber?.errors || {};
    const { ...restErrors } = currentErrors;
    const customPhoneErrors = e.errors ? e.errors : null;
    const nextErrors = customPhoneErrors ? { ...restErrors, ...customPhoneErrors } : restErrors;
    this.phoneNumber?.setErrors(Object.keys(nextErrors).length ? nextErrors : null);
  }

  get phoneNumber() {
    return this.signUpForm.get('phone_no');
  }

  get countryCode() {
    return this.signUpForm.get('country_code');
  }
}
